import { useState } from 'react';
import BaseAssignToolDataTable from '../BaseAssignToolDataTable';
import { GridFilterModel, GridCellParams } from '@mui/x-data-grid-premium';
import {
  useFetchAllDescriptionGroupsQuery,
  useFetchDescriptionGroupQuery,
  useCreateDescriptionGroupMutation,
  useUpdateDescriptionGroupMutation,
  useDeleteDescriptionGroupsMutation,
} from '../../slices/descriptionGroupSlice';
import { groupNameValidationSchema } from '../../utils/validation';
import { IconButton } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DescriptionGroup } from '../../types/groups';
import { GlobalAssetType, MaxAllowableContent } from '../../types/enums';
import { Description } from '../../types/pinnable';
import * as Yup from 'yup';

interface initialValuesShape {
  description_group: DescriptionGroup;
  members: Description[];
}

const HeadlineGroupSection = () => {
  const [deleteDescriptionGroups] = useDeleteDescriptionGroupsMutation();
  const [updateDescriptionGroup] = useUpdateDescriptionGroupMutation();
  const [createDescriptionGroup] = useCreateDescriptionGroupMutation();
  const [selectedId, setSelectedId] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 24,
  });
  const [_, setQueryOptions] = useState<{
    filterModel?: GridFilterModel;
  }>();

  const initialValues: initialValuesShape = {
    description_group: {
      id: '',
      group_name: '',
      assigned_to_count: 0,
      description_ids: [],
    },
    members: [],
  };
  const { data: selectedIdData = initialValues } =
    useFetchDescriptionGroupQuery(
      { descriptionGroupId: selectedId },
      {
        skip: !selectedId,
      }
    );
  const formatSelectedIdData = (selectedIdData: initialValuesShape) => ({
    id: selectedIdData.description_group.id,
    group_name: selectedIdData.description_group.group_name,
    descriptions: selectedIdData.members,
  });
  const { data = { total: 0, results: [] }, isFetching } =
    useFetchAllDescriptionGroupsQuery();

  const getColumns = (handleOpen: (id: string) => void) => [
    {
      field: 'id',
      headerName: 'Edit',
      width: 90,
      filterable: false,
      renderCell: (params: GridCellParams) => {
        return (
          <IconButton
            onClick={() => {
              handleOpen(params.row.id);
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        );
      },
    },
    {
      field: 'group_name',
      flex: 1,
      headerName: 'Description Group Name',
      editable: true,
    },
    {
      field: 'member_count',
      headerName: '# of members',
      width: 110,
      renderCell: (params: GridCellParams) => {
        return params.row.descriptions.length;
      },
    },
    {
      field: 'assigned_to_count',
      headerName: 'Assigned To',
      width: 90,
    },
  ];

  const validationSchema = Yup.object().shape({
    group_name: groupNameValidationSchema,
  });

  const fieldNames = ['group_name'];
  return (
    <BaseAssignToolDataTable
      data={data}
      isFetching={isFetching}
      assetType={GlobalAssetType.DescriptionGroup}
      getColumns={getColumns}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      setQueryOptions={setQueryOptions}
      updateAsset={async (asset: any) => {
        await updateDescriptionGroup(asset).unwrap();
      }}
      deleteAssets={deleteDescriptionGroups}
      createAsset={async (asset: any) => {
        await createDescriptionGroup(asset).unwrap();
      }}
      selectedIdData={formatSelectedIdData(selectedIdData)}
      selectedId={selectedId}
      setSelectedId={setSelectedId}
      validationSchema={validationSchema}
      fieldNames={fieldNames}
      maxAllowableContent={MaxAllowableContent.Descriptions}
    />
  );
};

export default HeadlineGroupSection;
